import React, { useState, useEffect, useRef, useCallback } from 'react';

const WEBHOOK_URL_KEY = '3t_webhook_url';
const CLIENT_ID_KEY = '3t_client_id';
const RUN_HISTORY_KEY = '3t_run_history';

const STATUS = {
  IDLE: 'idle',
  SCRAPING: 'scraping',
  PROCESSING: 'processing',
  COMPLETE: 'complete',
  ERROR: 'error',
};

const STATUS_LABEL = {
  [STATUS.IDLE]: 'Ready',
  [STATUS.SCRAPING]: 'Scraping posts…',
  [STATUS.PROCESSING]: 'Building messages…',
  [STATUS.COMPLETE]: 'Complete',
  [STATUS.ERROR]: 'Error',
};

const C = {
  bg: '#f6f6f4',
  surface: '#ffffff',
  surfaceHover: '#fafafa',
  border: '#e8e8e8',
  borderMid: '#d8d8d8',
  text: '#1a1a1a',
  muted: '#999999',
  mutedMid: '#777777',
  accent: '#3d7c00',
  accentDim: 'rgba(61,124,0,0.08)',
  accentBorder: 'rgba(61,124,0,0.25)',
  danger: '#dc2626',
  dangerDim: 'rgba(220,38,38,0.08)',
  warn: '#d97706',
  warnDim: 'rgba(217,119,6,0.08)',
  strong: '#3d7c00',
  moderate: '#d97706',
  weak: '#dc2626',
};

const css = {
  app: {
    fontFamily: "'DM Sans', sans-serif",
    background: C.bg,
    minHeight: '100vh',
    color: C.text,
    fontSize: 14,
  },
  header: {
    borderBottom: `1px solid ${C.border}`,
    padding: '0 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 56,
    position: 'sticky',
    top: 0,
    background: C.bg,
    zIndex: 100,
  },
  logoMark: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 13,
    letterSpacing: '0.08em',
    color: C.accent,
    fontWeight: 500,
  },
  nav: {
    display: 'flex',
    gap: 4,
  },
  navBtn: (active) => ({
    background: active ? C.accentDim : 'transparent',
    border: `1px solid ${active ? C.accentBorder : 'transparent'}`,
    color: active ? C.accent : C.muted,
    padding: '5px 14px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 400,
    transition: 'all 0.15s',
  }),
  main: {
    maxWidth: 860,
    margin: '0 auto',
    padding: '40px 32px',
  },
  card: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    padding: '24px 28px',
    marginBottom: 16,
  },
  label: {
    fontSize: 11,
    fontFamily: "'DM Mono', monospace",
    letterSpacing: '0.1em',
    color: C.muted,
    textTransform: 'uppercase',
    marginBottom: 8,
    display: 'block',
  },
  input: {
    width: '100%',
    background: C.bg,
    border: `1px solid ${C.borderMid}`,
    borderRadius: 6,
    color: C.text,
    padding: '9px 12px',
    fontSize: 13,
    fontFamily: "'DM Mono', monospace",
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  },
  dropzone: (active, hasFile) => ({
    border: `1.5px dashed ${active ? C.accent : hasFile ? C.accentBorder : C.borderMid}`,
    borderRadius: 8,
    padding: '32px 24px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.15s',
    background: active ? C.accentDim : 'transparent',
  }),
  btn: (variant = 'primary', disabled = false) => ({
    background: variant === 'primary'
      ? disabled ? 'rgba(200,255,90,0.15)' : C.accent
      : 'transparent',
    color: variant === 'primary'
      ? disabled ? 'rgba(61,124,0,0.4)' : '#ffffff'
      : C.muted,
    border: variant === 'ghost' ? `1px solid ${C.border}` : 'none',
    padding: variant === 'primary' ? '10px 24px' : '8px 16px',
    borderRadius: 6,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13,
    fontWeight: 500,
    fontFamily: "'DM Sans', sans-serif",
    transition: 'all 0.15s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  }),
  tag: (color) => {
    const map = {
      strong: { bg: 'rgba(200,255,90,0.1)', text: C.accent },
      moderate: { bg: 'rgba(255,180,68,0.1)', text: C.warn },
      weak: { bg: 'rgba(255,90,90,0.1)', text: C.danger },
      send: { bg: 'rgba(200,255,90,0.1)', text: C.accent },
      hold: { bg: 'rgba(255,90,90,0.1)', text: C.danger },
    };
    const t = map[color] || map.moderate;
    return {
      background: t.bg,
      color: t.text,
      fontSize: 11,
      fontFamily: "'DM Mono', monospace",
      letterSpacing: '0.06em',
      padding: '3px 8px',
      borderRadius: 4,
      display: 'inline-block',
    };
  },
  progressBar: {
    height: 3,
    background: C.borderMid,
    borderRadius: 2,
    overflow: 'hidden',
    marginTop: 10,
  },
  progressFill: (pct, color = C.accent) => ({
    height: '100%',
    width: `${pct}%`,
    background: color,
    borderRadius: 2,
    transition: 'width 0.6s ease',
  }),
  divider: {
    height: 1,
    background: C.border,
    margin: '20px 0',
  },
  row: {
    display: 'flex',
    gap: 16,
    alignItems: 'flex-start',
  },
  col: {
    flex: 1,
  },
  messageBox: {
    background: C.bg,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    padding: '12px 14px',
    fontSize: 13,
    lineHeight: 1.65,
    fontFamily: "'DM Sans', sans-serif",
    color: C.text,
    whiteSpace: 'pre-wrap',
    marginTop: 8,
  },
  signalBox: {
    background: C.bg,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    padding: '10px 14px',
    fontSize: 12,
    lineHeight: 1.6,
    color: C.mutedMid,
    marginTop: 8,
    fontStyle: 'italic',
  },
  historyRow: (isEven) => ({
    padding: '14px 28px',
    background: isEven ? 'transparent' : C.surfaceHover,
    borderBottom: `1px solid ${C.border}`,
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    cursor: 'pointer',
    transition: 'background 0.1s',
  }),
};

function parseCSV(text) {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, '').toLowerCase().replace(/\s+/g, '_'));
  return lines.slice(1).map(line => {
    const vals = [];
    let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') { inQ = !inQ; continue; }
      if (c === ',' && !inQ) { vals.push(cur.trim()); cur = ''; continue; }
      cur += c;
    }
    vals.push(cur.trim());
    const obj = {};
    headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
    return obj;
  }).filter(r => r.first_name || r.linkedin_url);
}

function Spinner() {
  return (
    <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', border: `2px solid rgba(200,255,90,0.2)`, borderTopColor: C.accent, animation: 'spin 0.7s linear infinite' }} />
  );
}

function RunResultsView({ run, onClose }) {
  const [filter, setFilter] = useState('all');
  const results = run.results || [];
  const sendable = results.filter(r => r.sendable);
  const weak = results.filter(r => !r.sendable);
  const shown = filter === 'all' ? results : filter === 'send' ? sendable : weak;

  const downloadCSV = () => {
    if (!run.csv_base64) return;
    const blob = new Blob([atob(run.csv_base64)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = run.filename || `3T_${run.date}.csv`;
    a.click();
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <span style={{ fontSize: 12, color: C.muted, fontFamily: "'DM Mono', monospace" }}>{run.date}</span>
          <div style={{ marginTop: 4, display: 'flex', gap: 8 }}>
            <span style={css.tag('send')}>{sendable.length} sendable</span>
            {weak.length > 0 && <span style={css.tag('weak')}>{weak.length} held</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {run.csv_base64 && (
            <button style={css.btn('ghost')} onClick={downloadCSV}>
              ↓ Export CSV
            </button>
          )}
          {onClose && <button style={css.btn('ghost')} onClick={onClose}>← Back</button>}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
        {[['all', 'All'], ['send', 'Sendable'], ['hold', 'Held']].map(([k, l]) => (
          <button key={k} style={css.navBtn(filter === k)} onClick={() => setFilter(k)}>{l}</button>
        ))}
      </div>

      {shown.map((r, i) => (
        <div key={i} style={{ ...css.card, marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <span style={{ fontWeight: 500, fontSize: 15 }}>{r.prospect_name}</span>
              <span style={{ color: C.muted, marginLeft: 8, fontSize: 13 }}>{r.title}</span>
              {r.company && <span style={{ color: C.muted, marginLeft: 4, fontSize: 13 }}>· {r.company}</span>}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <span style={css.tag(r.confidence_flag?.toLowerCase())}>{r.confidence_flag}</span>
              <span style={css.tag(r.sendable ? 'send' : 'hold')}>{r.sendable ? 'SEND' : 'HOLD'}</span>
            </div>
          </div>

          {r.signal_analysis && (
            <div style={css.signalBox}>{r.signal_analysis}</div>
          )}

          {r.message && (
            <div style={css.messageBox}>{r.message}</div>
          )}

          {r.weak_reason && (
            <div style={{ marginTop: 8, fontSize: 12, color: C.danger }}>
              Hold reason: {r.weak_reason}
            </div>
          )}

          {r.linkedin_url && r.sendable && (
            <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: C.muted, fontFamily: "'DM Mono', monospace", wordBreak: 'break-all' }}>
                {r.linkedin_url}
              </span>
            </div>
          )}
        </div>
      ))}

      {shown.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: C.muted }}>
          No results in this category.
        </div>
      )}
    </div>
  );
}

function HistoryView({ history, onSelect }) {
  if (history.length === 0) {
    return (
      <div style={{ ...css.card, textAlign: 'center', padding: '48px 0', color: C.muted }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>◌</div>
        No runs yet. Submit a batch to get started.
      </div>
    );
  }

  return (
    <div style={{ ...css.card, padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '14px 28px', borderBottom: `1px solid ${C.border}`, display: 'flex', gap: 16 }}>
        <span style={{ flex: 2, fontSize: 11, fontFamily: "'DM Mono', monospace", color: C.muted, letterSpacing: '0.08em' }}>DATE</span>
        <span style={{ flex: 1, fontSize: 11, fontFamily: "'DM Mono', monospace", color: C.muted, letterSpacing: '0.08em' }}>CLIENT</span>
        <span style={{ width: 80, fontSize: 11, fontFamily: "'DM Mono', monospace", color: C.muted, letterSpacing: '0.08em' }}>TOTAL</span>
        <span style={{ width: 80, fontSize: 11, fontFamily: "'DM Mono', monospace", color: C.muted, letterSpacing: '0.08em' }}>SENT</span>
        <span style={{ width: 60, fontSize: 11, fontFamily: "'DM Mono', monospace", color: C.muted, letterSpacing: '0.08em' }}>STATUS</span>
      </div>
      {history.map((run, i) => (
        <div key={run.id} style={css.historyRow(i % 2 === 0)} onClick={() => onSelect(run)}>
          <span style={{ flex: 2, fontSize: 13, fontFamily: "'DM Mono', monospace", color: C.mutedMid }}>{run.date}</span>
          <span style={{ flex: 1, fontSize: 13, color: C.text }}>{run.clientId || '—'}</span>
          <span style={{ width: 80, fontSize: 13 }}>{run.total || 0}</span>
          <span style={{ width: 80, fontSize: 13, color: C.accent }}>{run.sendable || 0}</span>
          <span style={{ width: 60 }}>
            <span style={css.tag(run.status === STATUS.COMPLETE ? 'send' : run.status === STATUS.ERROR ? 'weak' : 'moderate')}>
              {run.status === STATUS.COMPLETE ? 'done' : run.status === STATUS.ERROR ? 'err' : 'run'}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

export default function App() {
  const [view, setView] = useState('run');
  const [webhookUrl, setWebhookUrl] = useState(() => localStorage.getItem(WEBHOOK_URL_KEY) || '');
  const [clientId, setClientId] = useState(() => localStorage.getItem(CLIENT_ID_KEY) || '1');
  const [csvFile, setCsvFile] = useState(null);
  const [csvText, setCsvText] = useState('');
  const [prospects, setProspects] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [status, setStatus] = useState(STATUS.IDLE);
  const [statusMsg, setStatusMsg] = useState('');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [currentRun, setCurrentRun] = useState(null);
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem(RUN_HISTORY_KEY) || '[]'); } catch { return []; }
  });
  const [selectedRun, setSelectedRun] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    localStorage.setItem(WEBHOOK_URL_KEY, webhookUrl);
  }, [webhookUrl]);

  useEffect(() => {
    localStorage.setItem(CLIENT_ID_KEY, clientId);
  }, [clientId]);

  useEffect(() => {
    localStorage.setItem(RUN_HISTORY_KEY, JSON.stringify(history));
  }, [history]);

  const handleFile = useCallback((file) => {
    if (!file || !file.name.endsWith('.csv')) return;
    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      setCsvText(text);
      const parsed = parseCSV(text);
      setProspects(parsed);
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const simulateProgress = (start, end, duration, onDone) => {
    const step = (end - start) / (duration / 300);
    let cur = start;
    const interval = setInterval(() => {
      cur = Math.min(cur + step + Math.random() * 2, end);
      setProgress(Math.round(cur));
      if (cur >= end) {
        clearInterval(interval);
        if (onDone) onDone();
      }
    }, 300);
    return interval;
  };

  const handleSubmit = async () => {
    if (!webhookUrl || !csvText || prospects.length === 0) return;

    setStatus(STATUS.SCRAPING);
    setError(null);
    setProgress(0);

    const runId = `run_${Date.now()}`;
    const startDate = new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

    const scrapeInterval = simulateProgress(0, 60, 45000, () => {
      setStatus(STATUS.PROCESSING);
      setStatusMsg('Calling Claude API for each prospect…');
      simulateProgress(60, 95, 30000);
    });

    setStatusMsg('Scraping LinkedIn posts via Apify…');

    try {
      const res = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csv: csvText, client_id: clientId }),
      });

      clearInterval(scrapeInterval);

      if (!res.ok) throw new Error(`Webhook returned ${res.status}`);

      const data = await res.json();
      setProgress(100);
      setStatus(STATUS.COMPLETE);
      setStatusMsg('');

      // Parse results from response
      let results = [];
      if (data.results) {
        results = data.results;
      } else if (data.csv_base64) {
        // Decode CSV and parse rows for display
        const decoded = atob(data.csv_base64);
        const lines = decoded.trim().split('\n');
        lines.slice(1).forEach(line => {
          if (!line.trim() || line.startsWith('SENDABLE') || line.startsWith('WEAK')) return;
          const vals = [];
          let cur = '', inQ = false;
          for (let ch of line) {
            if (ch === '"') { inQ = !inQ; continue; }
            if (ch === ',' && !inQ) { vals.push(cur); cur = ''; continue; }
            cur += ch;
          }
          vals.push(cur);
          if (vals[1]) {
            results.push({
              sendable: vals[0]?.includes('Sendable'),
              prospect_name: vals[1],
              company: vals[2],
              title: vals[3],
              linkedin_url: vals[4],
              client_name: vals[5],
              confidence_flag: vals[6],
              signal_analysis: vals[7],
              message: vals[8],
              heyreach_row: vals[9],
              weak_reason: vals[10],
            });
          }
        });
      }

      const run = {
        id: runId,
        date: startDate,
        clientId,
        total: data.total || prospects.length,
        sendable: data.sendable_count || results.filter(r => r.sendable).length,
        weak: data.weak_count || results.filter(r => !r.sendable).length,
        status: STATUS.COMPLETE,
        results,
        csv_base64: data.csv_base64,
        filename: data.filename,
      };

      setCurrentRun(run);
      setHistory(h => [run, ...h]);

    } catch (err) {
      clearInterval(scrapeInterval);
      setStatus(STATUS.ERROR);
      setError(err.message);
      setProgress(0);

      const run = {
        id: runId,
        date: startDate,
        clientId,
        total: prospects.length,
        sendable: 0,
        weak: 0,
        status: STATUS.ERROR,
        results: [],
      };
      setHistory(h => [run, ...h]);
    }
  };

  const reset = () => {
    setCsvFile(null);
    setCsvText('');
    setProspects([]);
    setStatus(STATUS.IDLE);
    setStatusMsg('');
    setProgress(0);
    setError(null);
    setCurrentRun(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const isRunning = status === STATUS.SCRAPING || status === STATUS.PROCESSING;
  const canSubmit = webhookUrl && csvText && prospects.length > 0 && !isRunning;

  return (
    <div style={css.app}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
        button:hover { opacity: 0.85; }
        input:focus { border-color: ${C.accentBorder} !important; }
        ::selection { background: rgba(61,124,0,0.15); }
      `}</style>

      <header style={css.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <span style={css.logoMark}>3T / FACTOR15</span>
          <div style={css.nav}>
            <button style={css.navBtn(view === 'run')} onClick={() => { setView('run'); setSelectedRun(null); }}>Batch Run</button>
            <button style={css.navBtn(view === 'history')} onClick={() => { setView('history'); setSelectedRun(null); }}>History</button>
          </div>
        </div>
        <button style={css.navBtn(showSettings)} onClick={() => setShowSettings(s => !s)}>
          Settings
        </button>
      </header>

      <main style={css.main}>
        {/* Settings panel */}
        {showSettings && (
          <div style={{ ...css.card, borderColor: C.accentBorder, marginBottom: 24 }}>
            <span style={css.label}>Webhook URL</span>
            <input
              style={css.input}
              type="text"
              value={webhookUrl}
              onChange={e => setWebhookUrl(e.target.value)}
              placeholder="https://your-n8n.com/webhook/3t-run"
              spellCheck={false}
            />
            <div style={{ height: 16 }} />
            <span style={css.label}>Client ID</span>
            <input
              style={{ ...css.input, width: 120 }}
              type="text"
              value={clientId}
              onChange={e => setClientId(e.target.value)}
              placeholder="1"
              spellCheck={false}
            />
            <p style={{ marginTop: 12, fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
              Settings persist in browser localStorage. Webhook URL is your n8n <code style={{ fontFamily: "'DM Mono', monospace", color: C.mutedMid }}>3t-run</code> webhook endpoint.
            </p>
          </div>
        )}

        {/* Run view */}
        {view === 'run' && (
          <>
            {status === STATUS.COMPLETE && currentRun ? (
              <>
                <div style={{ ...css.card, borderColor: C.accentBorder, display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div style={{ display: 'flex', gap: 24 }}>
                    <div>
                      <div style={{ fontSize: 11, fontFamily: "'DM Mono', monospace", color: C.muted, letterSpacing: '0.08em', marginBottom: 4 }}>TOTAL</div>
                      <div style={{ fontSize: 28, fontWeight: 600, color: C.text }}>{currentRun.total}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontFamily: "'DM Mono', monospace", color: C.muted, letterSpacing: '0.08em', marginBottom: 4 }}>SENDABLE</div>
                      <div style={{ fontSize: 28, fontWeight: 600, color: C.accent }}>{currentRun.sendable}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontFamily: "'DM Mono', monospace", color: C.muted, letterSpacing: '0.08em', marginBottom: 4 }}>HELD</div>
                      <div style={{ fontSize: 28, fontWeight: 600, color: C.weak }}>{currentRun.weak}</div>
                    </div>
                  </div>
                  <button style={css.btn('ghost')} onClick={reset}>New Batch</button>
                </div>
                <RunResultsView run={currentRun} />
              </>
            ) : (
              <>
                {/* Upload card */}
                <div style={css.card}>
                  <span style={css.label}>Prospect CSV</span>
                  <div
                    style={css.dropzone(dragOver, !!csvFile)}
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
                    {csvFile ? (
                      <div>
                        <div style={{ color: C.accent, fontFamily: "'DM Mono', monospace", fontSize: 13, marginBottom: 4 }}>
                          {csvFile.name}
                        </div>
                        <div style={{ color: C.muted, fontSize: 12 }}>
                          {prospects.length} prospect{prospects.length !== 1 ? 's' : ''} detected
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div style={{ fontSize: 24, marginBottom: 8, color: C.borderMid }}>⊕</div>
                        <div style={{ color: C.muted, fontSize: 13 }}>Drop CSV or click to browse</div>
                        <div style={{ color: C.muted, fontSize: 11, marginTop: 4, fontFamily: "'DM Mono', monospace" }}>
                          first_name, last_name, company, title, linkedin_url, time_in_role, company_linkedin_url
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Preview */}
                {prospects.length > 0 && (
                  <div style={{ ...css.card, padding: 0, overflow: 'hidden' }}>
                    <div style={{ padding: '12px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 11, fontFamily: "'DM Mono', monospace", color: C.muted, letterSpacing: '0.08em' }}>PREVIEW — {prospects.length} PROSPECTS</span>
                      <button style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 12 }} onClick={reset}>Clear</button>
                    </div>
                    {prospects.slice(0, 5).map((p, i) => (
                      <div key={i} style={{ padding: '10px 20px', borderBottom: i < Math.min(prospects.length, 5) - 1 ? `1px solid ${C.border}` : 'none', display: 'flex', gap: 12, alignItems: 'center' }}>
                        <span style={{ width: 20, fontSize: 11, color: C.muted, fontFamily: "'DM Mono', monospace" }}>{i + 1}</span>
                        <span style={{ flex: 2, fontSize: 13, fontWeight: 500 }}>{p.first_name} {p.last_name}</span>
                        <span style={{ flex: 2, fontSize: 13, color: C.mutedMid }}>{p.company}</span>
                        <span style={{ flex: 2, fontSize: 12, color: C.muted }}>{p.title}</span>
                        <span style={{ fontSize: 11, color: p.company_linkedin_url ? C.accent : C.muted, fontFamily: "'DM Mono', monospace" }}>
                          {p.company_linkedin_url ? '+ co feed' : 'personal only'}
                        </span>
                      </div>
                    ))}
                    {prospects.length > 5 && (
                      <div style={{ padding: '8px 20px', color: C.muted, fontSize: 12 }}>
                        +{prospects.length - 5} more
                      </div>
                    )}
                  </div>
                )}

                {/* Status / progress */}
                {isRunning && (
                  <div style={css.card}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <Spinner />
                      <span style={{ fontSize: 13, color: C.accent, fontFamily: "'DM Mono', monospace" }}>
                        {STATUS_LABEL[status]}
                      </span>
                    </div>
                    {statusMsg && <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>{statusMsg}</div>}
                    <div style={css.progressBar}>
                      <div style={css.progressFill(progress)} />
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 11, color: C.muted, fontFamily: "'DM Mono', monospace", marginTop: 4 }}>{progress}%</div>
                  </div>
                )}

                {error && (
                  <div style={{ ...css.card, borderColor: C.danger, background: C.dangerDim }}>
                    <span style={{ color: C.danger, fontSize: 13 }}>Error: {error}</span>
                  </div>
                )}

                {/* Submit */}
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <button
                    style={css.btn('primary', !canSubmit)}
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                  >
                    {isRunning ? <><Spinner /> Running…</> : `Run Batch${prospects.length > 0 ? ` (${prospects.length})` : ''}`}
                  </button>
                  {!webhookUrl && (
                    <span style={{ fontSize: 12, color: C.warn }}>
                      ↑ Set webhook URL in Settings first
                    </span>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {/* History view */}
        {view === 'history' && (
          selectedRun ? (
            <RunResultsView run={selectedRun} onClose={() => setSelectedRun(null)} />
          ) : (
            <HistoryView history={history} onSelect={run => setSelectedRun(run)} />
          )
        )}
      </main>
    </div>
  );
}
